import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { websocketClient } from '@/lib/websocket-client';
import { apiClient } from '@/lib/api-client';

export interface Prize {
  id: number;
  name: string;
  description: string | null;
  category: string;
  estimated_value: number;
  images: string[];
  status: string;
  scheduled_end_at: string | null;
  total_entries: number;
  my_entries: number;
}

export interface PrizeEntryTier {
  id: number;
  sc_amount: number;
  entries_awarded: number;
  display_order: number;
}

export interface PrizeWinner {
  id: number;
  prize_id: number;
  prize_name: string;
  prize_images: string[];
  prize_value: number;
  winner_name: string;
  created_at: string;
  claim_status: string | null;
  claim_deadline?: string;
  is_me?: boolean;
}

export interface PrizeClaim {
  id: number;
  prize_winner_id: number;
  full_name: string;
  email: string;
  phone: string | null;
  shipping_address: ShippingAddress;
  notes: string | null;
  status: string;
  tracking_number: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  created_at: string;
}

export interface ActiveClaim {
  winner_id: number;
  prize_name: string;
  prize_images: string[];
  prize_value: number;
  claim_status: string;
  tracking_number: string | null;
  created_at: string;
}

export interface ShippingAddress {
  street: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface PrizeContextValue {
  prizes: Prize[];
  myEntries: Record<number, number>;
  totalMyEntries: number;
  entryTiers: PrizeEntryTier[];
  unseenWins: PrizeWinner[];
  myActiveClaims: ActiveClaim[];
  publicWinners: PrizeWinner[];
  loading: boolean;
  error: string | null;
  loadPrizes: () => Promise<void>;
  loadPublicWinners: () => Promise<void>;
  purchaseEntries: (prizeId: number, scAmount: number) => Promise<{ entries: number; newTotal: number }>;
  getMyEntriesForPrize: (prizeId: number) => number;
  getPrizeById: (prizeId: number) => Prize | undefined;
  markWinAsSeen: (winnerId: number) => Promise<void>;
  submitClaim: (winnerId: number, data: ClaimFormData) => Promise<void>;
  getClaimForWinner: (winnerId: number) => Promise<PrizeClaim | null>;
}

export interface ClaimFormData {
  full_name: string;
  email: string;
  phone?: string;
  shipping_address: ShippingAddress;
  notes?: string;
}

const PrizeContext = createContext<PrizeContextValue | undefined>(undefined);

export function usePrizes() {
  const context = useContext(PrizeContext);
  if (!context) {
    throw new Error('usePrizes must be used within a PrizeProvider');
  }
  return context;
}

interface PrizeProviderProps {
  children: ReactNode;
}

export function PrizeProvider({ children }: PrizeProviderProps) {
  const { user } = useAuth();

  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [myEntries, setMyEntries] = useState<Record<number, number>>({});
  const [totalMyEntries, setTotalMyEntries] = useState(0);
  const [entryTiers, setEntryTiers] = useState<PrizeEntryTier[]>([]);
  const [unseenWins, setUnseenWins] = useState<PrizeWinner[]>([]);
  const [myActiveClaims, setMyActiveClaims] = useState<ActiveClaim[]>([]);
  const [publicWinners, setPublicWinners] = useState<PrizeWinner[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPrizes = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/prize/lobby');
      if (response?.success) {
        setPrizes(response.prizes || []);
        setMyEntries(response.myEntries || {});
        setTotalMyEntries(response.totalMyEntries || 0);
        setEntryTiers(response.entryTiers || []);
        setUnseenWins(response.unseenWins || []);
        setMyActiveClaims(response.myActiveClaims || []);
      }
    } catch (err: any) {
      console.error('Failed to load prizes:', err);
      setError(err.response?.message || err.message || 'Failed to load prizes');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const loadPublicWinners = useCallback(async () => {
    try {
      const response = await apiClient.get('/prize/winners');
      if (response?.success) {
        setPublicWinners(response.winners || []);
      }
    } catch (err) {
      console.error('Failed to load winners:', err);
    }
  }, []);

  const purchaseEntries = useCallback(
    async (prizeId: number, scAmount: number): Promise<{ entries: number; newTotal: number }> => {
      const response = await apiClient.post(`/prize/${prizeId}/enter`, { sc_amount: scAmount });
      if (response?.success) {
        const { entry, newTotalEntries } = response;

        setMyEntries((prev) => ({
          ...prev,
          [prizeId]: newTotalEntries,
        }));

        setTotalMyEntries((prev) => prev + entry.entries_count);

        setPrizes((prev) =>
          prev.map((p) =>
            p.id === prizeId
              ? { ...p, my_entries: newTotalEntries, total_entries: p.total_entries + entry.entries_count }
              : p
          )
        );

        return { entries: entry.entries_count, newTotal: newTotalEntries };
      }
      throw new Error(response?.message || 'Failed to purchase entries');
    },
    []
  );

  const getMyEntriesForPrize = useCallback(
    (prizeId: number): number => {
      return myEntries[prizeId] || 0;
    },
    [myEntries]
  );

  const getPrizeById = useCallback(
    (prizeId: number): Prize | undefined => {
      return prizes.find((p) => p.id === prizeId);
    },
    [prizes]
  );

  const markWinAsSeen = useCallback(async (winnerId: number) => {
    try {
      await apiClient.put(`/prize/wins/${winnerId}/seen`);
      setUnseenWins((prev) => prev.filter((w) => w.id !== winnerId));
    } catch (err) {
      console.error('Failed to mark win as seen:', err);
    }
  }, []);

  const submitClaim = useCallback(
    async (winnerId: number, data: ClaimFormData) => {
      const response = await apiClient.post(`/prize/winners/${winnerId}/claim`, data);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to submit claim');
      }
    },
    []
  );

  const getClaimForWinner = useCallback(async (winnerId: number): Promise<PrizeClaim | null> => {
    try {
      const response = await apiClient.get(`/prize/winners/${winnerId}/claim`);
      if (response?.success) {
        return response.claim || null;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadPrizes();
      loadPublicWinners();
    }
  }, [user, loadPrizes, loadPublicWinners]);

  useEffect(() => {
    if (!user) return;

    const unsubscribePrizeUpdate = websocketClient.subscribe('prize:update', (data: any) => {
      setPrizes((prev) =>
        prev.map((p) => (p.id === data.id ? { ...p, ...data } : p))
      );
    });

    const unsubscribePrizeWin = websocketClient.subscribe('prize:win', (data: any) => {
      if (data.is_me) {
        setUnseenWins((prev) => [data as PrizeWinner, ...prev]);
      }
      setPublicWinners((prev) => [data as PrizeWinner, ...prev.slice(0, 19)]);
      loadPrizes();
    });

    return () => {
      unsubscribePrizeUpdate();
      unsubscribePrizeWin();
    };
  }, [user, loadPrizes]);

  const value: PrizeContextValue = {
    prizes,
    myEntries,
    totalMyEntries,
    entryTiers,
    unseenWins,
    myActiveClaims,
    publicWinners,
    loading,
    error,
    loadPrizes,
    loadPublicWinners,
    purchaseEntries,
    getMyEntriesForPrize,
    getPrizeById,
    markWinAsSeen,
    submitClaim,
    getClaimForWinner,
  };

  return <PrizeContext.Provider value={value}>{children}</PrizeContext.Provider>;
}

export default PrizeContext;
